import {
  buildCatalogEquivalenceGroups,
  getCatalogVitrinePrimary,
} from './catalogEquivalenceGroup';
import { copyTextToClipboard } from './formatProductCatalogStructureForCopy';

export { copyTextToClipboard };

function formatProductTypeLabel(type) {
  if (type === 'plan') return 'Plano';
  if (type === 'course') return 'Curso';
  return 'Produto';
}

function formatVisibleLabel(visible) {
  return visible ? 'sim' : 'não (oculto da vitrine)';
}

/** Agrupa produtos/planos/cursos por equivalentIds (1 card na vitrine por grupo). */
export function buildVitrineProductGroupsForAi(catalogProducts = [], productCategories = []) {
  const categoryNameById = new Map(productCategories.map((category) => [category.id, category.name]));
  const { groups } = buildCatalogEquivalenceGroups(catalogProducts);
  const uniqueGroups = [...groups.values()];

  return uniqueGroups
    .map((members) => {
      const anchor = members[0];
      const primary =
        getCatalogVitrinePrimary(anchor, catalogProducts) ||
        [...members].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];

      const sortedMembers = [...members].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR')
      );

      const equivalentLabels = sortedMembers
        .filter((member) => member.id !== primary.id)
        .map((member) => `${member.name || 'Sem nome'} (${formatProductTypeLabel(member.type)})`);

      const visibleInVitrine = members.some((member) => member.showInCatalog !== false);
      const categoryId =
        primary.productCategoryId ||
        members.find((member) => member.productCategoryId)?.productCategoryId ||
        null;

      return {
        displayName: primary.name || 'Sem nome',
        type: primary.type,
        visibleInVitrine,
        currentCategory: categoryId ? categoryNameById.get(categoryId) ?? null : null,
        equivalents: equivalentLabels,
      };
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'pt-BR'));
}

function formatProductCategoriesList(productCategories = []) {
  if (!productCategories.length) {
    return '(nenhuma categoria de produtos criada ainda)';
  }

  return productCategories
    .map((category) => `- ${category.name}`)
    .join('\n');
}

function formatVitrineProductGroupsList(groups = []) {
  if (!groups.length) {
    return '(nenhum produto/plano/curso cadastrado)';
  }

  return groups
    .map((group) => {
      const lines = [
        `- Produto na vitrine: ${group.displayName}`,
        `  Tipo: ${formatProductTypeLabel(group.type)}`,
        `  Visível na vitrine: ${formatVisibleLabel(group.visibleInVitrine)}`,
        `  Categoria atual: ${group.currentCategory || '(nenhuma)'}`,
        `  Equivalentes no grupo (mesmo card na Home): ${
          group.equivalents.length ? group.equivalents.join('; ') : '(nenhum)'
        }`,
      ];
      return lines.join('\n');
    })
    .join('\n\n');
}

/**
 * Prompt para colar em IA externa e sugerir categorias de produtos na vitrine (sem capas).
 */
export function buildProductCategoryAiAssistantText(
  catalogProducts = [],
  productCategories = []
) {
  const groups = buildVitrineProductGroupsForAi(catalogProducts, productCategories);
  const categoriesList = formatProductCategoriesList(productCategories);
  const productsList = formatVitrineProductGroupsList(groups);

  return `Tenho uma vitrine de produtos no app. Preciso organizar em quais categorias de produtos (prateleiras da vitrine) cada item aparece na Home.

Categorias de produtos já criadas no painel:
${categoriesList}

Produtos da vitrine (equivalentes agrupados — o aluno vê 1 card na Home por grupo):
${productsList}

Sugira como organizar as categorias de produtos da vitrine. NÃO gere capas de imagem nesta conversa — as capas são geradas produto a produto no painel (Produtos → editar → Vitrine de Produtos → Gerar capa com IA).

Como pensar:
- Categorias de produtos são prateleiras na Home (ex.: formações, assinaturas, cursos avulsos) — nomes orientados ao aluno
- Cada linha "Produto na vitrine" é um grupo: produtos equivalentes compartilham o mesmo card; atribua UMA categoria por grupo
- Pode reutilizar categorias já criadas ou sugerir novas (nomes curtos e claros)
- Produtos ocultos da vitrine podem receber categoria para quando forem publicados

Regras:
- Ao atribuir produtos, use o título EXATO de "Produto na vitrine" da lista acima
- Ao citar categorias existentes, use o nome EXATO da lista de categorias já criadas
- Se sugerir categoria nova, indique claramente como "(nova)"
- Um grupo equivalente = uma única categoria

Formato da resposta (obrigatório, em português):

1) Categorias de produtos sugeridas:

- … (nova)
- … (existente: copie o nome exato)
(ou "Usar apenas as existentes" se bastar)

2) Atribuição por produto (1 bloco por grupo da lista):

Produto na vitrine: …
Categoria de produtos: …

3) Produtos visíveis na vitrine ainda sem categoria sugerida:

- …
(ou "Nenhum")

4) Observações (opcional):

…

O admin vai selecionar manualmente a categoria em cada produto no painel (Produtos → editar → Vitrine de Produtos). Para capas 16:9, use "Gerar capa com IA" dentro de cada produto.`;
}
